"use client";
import { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { useAuthenticated } from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { BuilderResourceAssignments } from "./BuilderResourceAssignments";
import { BuilderResourceBulkForm } from "./BuilderResourceBulkForm";
import { BuilderResourceForm } from "./BuilderResourceForm";
import { BuilderResourceHours } from "./BuilderResourceHours";

const TABS = [
  { id: "general", title: "General" },
  { id: "hours", title: "Hours" },
  { id: "restrictions", title: "Assignments" }
] as const;

type TabId = (typeof TABS)[number]["id"];

/**
 * Polished resource editor for the map builder's resources panel — the
 * modern replacement for the legacy ResourcePanel/GeneralTab stack (which
 * other screens still use, so those components stay untouched). Same
 * behavior: single-resource form, bulk edit with naming patterns, hours
 * and assignment restrictions.
 */
export const BuilderResourceEditor: React.FC<{
  resources: IResource[];
  /** Called with the resources just saved so the caller can mirror the
   *  new name/label onto the matching canvas spot shapes. */
  onResourcesSaved?: (
    updates: { id: string; name: string; label?: string | null }[]
  ) => void;
}> = ({ resources, onResourcesSaved }) => {
  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();

  // The office the edited resources belong to — the builder's floor
  // switcher can target an office other than the user's current one, and
  // hour rules are office-scoped.
  const resourceOffice =
    (resources[0]?.office_id
      ? authenticatedUser?.offices?.byId?.[resources[0].office_id]
      : undefined) ?? currentOffice;
  const queryClient = useQueryClient();
  const [tab, setTab] = React.useState<TabId>("general");

  const afterEdit = async () => {
    // Invalidate for the office the edited resources belong to — the
    // builder's floor switcher can target an office other than the
    // user's current one.
    const officeId = resources[0]?.office_id ?? currentOffice.id;
    queryClient.invalidateQueries({
      queryKey: ["organizations.resourcesAll", officeId]
    });
  };

  const isSingle = resources.length === 1;
  const resourcesKey = resources.map((x) => x.id).join(",");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Segmented tab control, matching the builder's toolbar style */}
      <div className="mb-3 flex flex-none rounded-lg bg-neutral-100 p-0.5">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "flex-1 rounded-md px-2 py-1.5 font-medium text-xs transition-colors",
              tab === t.id
                ? "bg-white text-neutral-900 shadow-sm"
                : "text-neutral-500 hover:text-neutral-700"
            )}
          >
            {t.title}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1">
        {tab === "general" &&
          (isSingle ? (
            <BuilderResourceForm
              key={resources[0].id}
              resource={resources[0]}
              mutate={afterEdit}
              onResourcesSaved={onResourcesSaved}
            />
          ) : (
            <BuilderResourceBulkForm
              key={resourcesKey}
              resources={resources}
              mutate={afterEdit}
              onResourcesSaved={onResourcesSaved}
            />
          ))}
        {tab === "hours" && (
          <BuilderResourceHours
            resources={resources}
            office={resourceOffice}
            key={resourcesKey}
          />
        )}
        {tab === "restrictions" && (
          <BuilderResourceAssignments
            mutateAll={afterEdit}
            resources={resources}
            key={resourcesKey}
          />
        )}
      </div>
    </div>
  );
};
